import React, { useState, useEffect, useContext, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import axios from "axios";
import { AuthContext, API } from "../App";
import { Button } from "../components/ui/button";
import { Slider } from "../components/ui/slider";
import { 
  ArrowLeft, Wind, Heart, Moon, Sun, Play, Pause, 
  RotateCcw, Check, Volume2, Clock, Sparkles
} from "lucide-react";
import { toast } from "sonner";
import { PremiumPageGuard } from "../components/PremiumPageGuard";
import { devError } from "../utils/devLogger";
import AIMeditation from "../components/AIMeditation";
import SleepStories from "../components/SleepStories";

// Breathing Timer Component
const BreathingTimer = ({ exercise, onComplete, onBack, t }) => {
  const [phase, setPhase] = useState('inhale');
  const [timeLeft, setTimeLeft] = useState(exercise.pattern.inhale);
  const [cyclesLeft, setCyclesLeft] = useState(exercise.cycles);
  const [isRunning, setIsRunning] = useState(false);
  const [completed, setCompleted] = useState(false);

  const phases = [
    { name: 'inhale', duration: exercise.pattern.inhale, label: t('mindfulness.breatheIn'), color: 'bg-sky-400' },
    { name: 'hold1', duration: exercise.pattern.hold1, label: t('mindfulness.hold'), color: 'bg-indigo-400' },
    { name: 'exhale', duration: exercise.pattern.exhale, label: t('mindfulness.breatheOut'), color: 'bg-cyan-400' },
    { name: 'hold2', duration: exercise.pattern.hold2, label: t('mindfulness.hold'), color: 'bg-slate-400' }
  ].filter(p => p.duration > 0);

  useEffect(() => {
    if (!isRunning || completed) return;

    const timer = setInterval(() => {
      setTimeLeft(prev => {
        if (prev <= 1) {
          const currentPhaseIndex = phases.findIndex(p => p.name === phase);
          const nextPhaseIndex = (currentPhaseIndex + 1) % phases.length;
          
          if (nextPhaseIndex === 0) {
            if (cyclesLeft <= 1) {
              setCompleted(true);
              setIsRunning(false);
              onComplete(exercise.cycles);
              return 0;
            }
            setCyclesLeft(prev => prev - 1);
          }
          
          setPhase(phases[nextPhaseIndex].name);
          return phases[nextPhaseIndex].duration;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [isRunning, phase, cyclesLeft, completed]); // eslint-disable-line react-hooks/exhaustive-deps

  const currentPhaseData = phases.find(p => p.name === phase);
  const progress = ((currentPhaseData.duration - timeLeft) / currentPhaseData.duration) * 100;

  const circleSize = 250;
  const strokeWidth = 8;
  const radius = (circleSize - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;
  const strokeDashoffset = circumference - (progress / 100) * circumference;

  return (
    <div className="text-center">
      <h2 className="text-2xl font-light text-sky-900 mb-2" style={{ fontFamily: 'Outfit' }}>
        {exercise.name}
      </h2>
      <p className="text-slate-500 mb-8">{exercise.description}</p>

      <div className="relative w-64 h-64 mx-auto mb-8">
        <svg width={circleSize} height={circleSize} className="transform -rotate-90">
          <circle cx={circleSize/2} cy={circleSize/2} r={radius} fill="none" stroke="#e0f2fe" strokeWidth={strokeWidth} />
          <circle
            cx={circleSize/2} cy={circleSize/2} r={radius} fill="none"
            stroke={currentPhaseData.color.replace('bg-', '#').replace('sky-400', '38bdf8').replace('indigo-400', '818cf8').replace('cyan-400', '22d3ee').replace('slate-400', '94a3b8')}
            strokeWidth={strokeWidth} strokeDasharray={circumference} strokeDashoffset={strokeDashoffset}
            strokeLinecap="round" className="transition-all duration-1000"
          />
        </svg>
        <div className="absolute inset-0 flex flex-col items-center justify-center">
          <p className={`text-3xl font-light ${currentPhaseData.color.replace('bg-', 'text-')}`} style={{ fontFamily: 'Outfit' }}>
            {timeLeft}
          </p>
          <p className="text-lg text-slate-600 mt-2">{currentPhaseData.label}</p>
          <p className="text-sm text-slate-400 mt-1">{cyclesLeft} {t('mindfulness.cyclesLeft')}</p>
        </div>
      </div>

      <div className="flex gap-4 justify-center">
        {!completed ? (
          <>
            <Button
              onClick={() => setIsRunning(!isRunning)}
              className={isRunning ? 'btn-secondary-glass' : 'glow-button'}
              size="lg"
            >
              {isRunning ? <Pause className="w-5 h-5 mr-2" /> : <Play className="w-5 h-5 mr-2" />}
              {isRunning ? t('common.pause') : t('common.start')}
            </Button>
            <Button
              variant="outline"
              onClick={() => {
                setPhase('inhale');
                setTimeLeft(exercise.pattern.inhale);
                setCyclesLeft(exercise.cycles);
                setIsRunning(false);
              }}
            >
              <RotateCcw className="w-5 h-5" />
            </Button>
          </>
        ) : (
          <div className="space-y-4">
            <div className="flex items-center justify-center gap-2 text-green-500">
              <Check className="w-6 h-6" />
              <span className="text-lg">{t('mindfulness.exerciseComplete')}</span>
            </div>
            <Button onClick={onBack} className="glow-button">{t('common.back')}</Button>
          </div>
        )}
      </div>
    </div>
  );
};

// Meditation Timer Component
const MeditationTimer = ({ session, onComplete, onBack, t }) => {
  const [timeLeft, setTimeLeft] = useState(session.duration_minutes * 60);
  const [isRunning, setIsRunning] = useState(false);
  const [completed, setCompleted] = useState(false);
  const [moodBefore, setMoodBefore] = useState(5);
  const [moodAfter, setMoodAfter] = useState(null);
  const [phase, setPhase] = useState('setup');
  const audioRef = useRef(null);

  useEffect(() => {
    if (!isRunning || completed) return;

    const timer = setInterval(() => {
      setTimeLeft(prev => {
        if (prev <= 1) {
          setIsRunning(false);
          setPhase('rating');
          if (audioRef.current) audioRef.current.pause();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [isRunning, completed]); // eslint-disable-line react-hooks/exhaustive-deps

  const togglePlayback = () => {
    const newRunning = !isRunning;
    setIsRunning(newRunning);
    if (audioRef.current) {
      if (newRunning) audioRef.current.play().catch(() => {});
      else audioRef.current.pause();
    }
  };

  const handleComplete = () => {
    setCompleted(true);
    if (audioRef.current) audioRef.current.pause();
    onComplete(session.duration_minutes * 60 - timeLeft, moodBefore, moodAfter);
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const progress = ((session.duration_minutes * 60 - timeLeft) / (session.duration_minutes * 60)) * 100;

  if (phase === 'setup') {
    return (
      <div className="text-center">
        <h2 className="text-2xl font-light text-sky-900 mb-2" style={{ fontFamily: 'Outfit' }}>
          {session.name}
        </h2>
        <p className="text-slate-500 mb-8">{session.description}</p>
        
        <div className="glass-card p-6 max-w-md mx-auto mb-8">
          <p className="text-slate-600 mb-4">{t('mindfulness.howFeeling')}</p>
          <div className="flex items-center gap-4">
            <span className="text-2xl">&#128532;</span>
            <Slider value={[moodBefore]} onValueChange={(v) => setMoodBefore(v[0])} max={10} min={1} step={1} className="flex-1" />
            <span className="text-2xl">&#128522;</span>
          </div>
          <p className="text-sky-500 text-lg mt-2">{moodBefore}/10</p>
        </div>

        <Button onClick={() => { setPhase('meditating'); togglePlayback(); }} className="glow-button" size="lg">
          <Play className="w-5 h-5 mr-2" /> {t('mindfulness.beginSession')}
        </Button>
        {session.audio_url && (
          <audio ref={audioRef} src={`${API.replace('/api', '')}${session.audio_url}`} loop preload="auto" />
        )}
      </div>
    );
  }

  if (phase === 'rating') {
    return (
      <div className="text-center">
        <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-green-100 flex items-center justify-center">
          <Check className="w-10 h-10 text-green-500" />
        </div>
        <h2 className="text-2xl font-light text-sky-900 mb-2" style={{ fontFamily: 'Outfit' }}>
          {t('mindfulness.sessionComplete')}
        </h2>
        
        <div className="glass-card p-6 max-w-md mx-auto mb-8">
          <p className="text-slate-600 mb-4">{t('mindfulness.howFeelingNow')}</p>
          <div className="flex items-center gap-4">
            <span className="text-2xl">&#128532;</span>
            <Slider value={[moodAfter || 5]} onValueChange={(v) => setMoodAfter(v[0])} max={10} min={1} step={1} className="flex-1" />
            <span className="text-2xl">&#128522;</span>
          </div>
          <p className="text-sky-500 text-lg mt-2">{moodAfter || 5}/10</p>
        </div>

        <Button onClick={handleComplete} className="glow-button" size="lg">
          <Check className="w-5 h-5 mr-2" /> {t('common.complete')}
        </Button>
      </div>
    );
  }

  return (
    <div className="text-center">
      <h2 className="text-2xl font-light text-sky-900 mb-8" style={{ fontFamily: 'Outfit' }}>
        {session.name}
      </h2>

      <div className="relative w-64 h-64 mx-auto mb-8">
        <div className="absolute inset-0 rounded-full bg-gradient-to-br from-sky-100 to-indigo-100 animate-pulse" 
             style={{ animationDuration: '4s' }} />
        <div className="absolute inset-4 rounded-full bg-white/80 flex flex-col items-center justify-center">
          <p className="text-4xl font-light text-sky-900" style={{ fontFamily: 'Outfit' }}>
            {formatTime(timeLeft)}
          </p>
          <p className="text-sm text-slate-500 mt-2">{t('mindfulness.remaining')}</p>
        </div>
      </div>

      <div className="w-64 mx-auto h-2 bg-sky-100 rounded-full overflow-hidden mb-8">
        <div 
          className="h-full bg-gradient-to-r from-sky-400 to-indigo-400 transition-all duration-1000"
          style={{ width: `${progress}%` }}
        />
      </div>

      <div className="flex gap-4 justify-center">
        <Button
          onClick={togglePlayback}
          className={isRunning ? 'btn-secondary-glass' : 'glow-button'}
          size="lg"
        >
          {isRunning ? <Pause className="w-5 h-5 mr-2" /> : <Play className="w-5 h-5 mr-2" />}
          {isRunning ? t('common.pause') : t('common.resume')}
        </Button>
        <Button variant="outline" onClick={() => { if (audioRef.current) audioRef.current.pause(); onBack(); }}>
          {t('mindfulness.endEarly')}
        </Button>
      </div>
      {session.audio_url && phase === 'meditating' && (
        <div className="flex items-center justify-center gap-2 mt-4 text-sm text-slate-500">
          <Volume2 className="w-4 h-4" />
          <span>{t('mindfulness.audioPlaying') || 'Audio guided session'}</span>
        </div>
      )}
      {!session.audio_url && phase === 'meditating' && (
        <audio ref={audioRef} />
      )}
    </div>
  );
};

// Main Mindfulness Page
const Mindfulness = () => {
  const { t } = useTranslation();
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const [tab, setTab] = useState('ai');
  const [sessions, setSessions] = useState([]);
  const [exercises, setExercises] = useState([]);
  const [activeSession, setActiveSession] = useState(null);
  const [activeExercise, setActiveExercise] = useState(null);
  const [history, setHistory] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    try {
      const [sessionsRes, exercisesRes, historyRes] = await Promise.all([
        axios.get(`${API}/meditation/sessions`),
        axios.get(`${API}/breathing/exercises`),
        axios.get(`${API}/mindfulness/history`)
      ]);
      setSessions(sessionsRes.data.sessions);
      setExercises(exercisesRes.data.exercises);
      setHistory(historyRes.data);
    } catch (error) {
      devError("Failed to fetch data:", error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleMeditationComplete = async (duration, moodBefore, moodAfter) => {
    try {
      await axios.post(`${API}/meditation/complete`, {
        session_id: activeSession.id,
        duration_completed_seconds: duration,
        mood_before: moodBefore,
        mood_after: moodAfter
      });
      
      const improvement = moodAfter - moodBefore;
      if (improvement > 0) {
        toast.success(t('notifications.moodImproved', { points: improvement }));
      } else {
        toast.success(t('notifications.meditationComplete'));
      }
      
      setActiveSession(null);
      fetchData();
    } catch (error) {
      toast.error("Failed to save session");
    }
  };

  const handleBreathingComplete = async (cycles) => {
    try {
      await axios.post(`${API}/breathing/complete`, {
        exercise_id: activeExercise.id,
        cycles_completed: cycles
      });
      toast.success(t('notifications.breathingComplete'));
      setActiveExercise(null);
      fetchData();
    } catch (error) {
      toast.error("Failed to save exercise");
    }
  };

  const getCategoryIcon = (category) => {
    switch(category) {
      case 'morning': return Sun;
      case 'sleep': return Moon;
      case 'stress': return Heart;
      default: return Wind;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-sky-50 flex items-center justify-center">
        <div className="w-16 h-16 rounded-full bg-sky-400/30 glow-pulse" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-sky-50" data-testid="mindfulness-page">
      <div className="fixed top-0 right-0 w-96 h-96 bg-indigo-300/10 rounded-full blur-3xl pointer-events-none" />
      
      <header className="fixed top-0 w-full z-50 glass-header">
        <div className="max-w-5xl mx-auto px-4 md:px-8 py-4">
          <div className="flex items-center justify-between">
            <Button
              variant="ghost"
              onClick={() => {
                if (activeSession) setActiveSession(null);
                else if (activeExercise) setActiveExercise(null);
                else navigate('/dashboard');
              }}
              className="text-sky-600 hover:text-sky-800"
              data-testid="back-btn"
            >
              <ArrowLeft className="w-5 h-5 mr-2" strokeWidth={1.5} />
              {activeSession || activeExercise ? t('common.back') : t('common.dashboard')}
            </Button>
            <div className="flex items-center gap-2">
              <Heart className="w-5 h-5 text-pink-400" strokeWidth={1.5} />
              <span className="text-sm text-sky-700">{t('mindfulness.title')}</span>
            </div>
          </div>
        </div>
      </header>

      <main className="pt-24 pb-12 px-4 md:px-8">
        <div className="max-w-5xl mx-auto">
          {!activeSession && !activeExercise ? (
            <>
              {history?.stats && (
                <div className="grid grid-cols-3 gap-4 mb-8">
                  <div className="glass-card p-4 text-center">
                    <p className="text-2xl font-light text-sky-900" style={{ fontFamily: 'Outfit' }}>
                      {history.stats.total_meditation_minutes}
                    </p>
                    <p className="text-xs text-slate-500">{t('mindfulness.minutesMeditated')}</p>
                  </div>
                  <div className="glass-card p-4 text-center">
                    <p className="text-2xl font-light text-pink-500" style={{ fontFamily: 'Outfit' }}>
                      {history.stats.meditation_sessions}
                    </p>
                    <p className="text-xs text-slate-500">{t('mindfulness.meditations')}</p>
                  </div>
                  <div className="glass-card p-4 text-center">
                    <p className="text-2xl font-light text-cyan-500" style={{ fontFamily: 'Outfit' }}>
                      {history.stats.breathing_sessions}
                    </p>
                    <p className="text-xs text-slate-500">{t('mindfulness.breathing')}</p>
                  </div>
                </div>
              )}

              <div className="flex gap-2 mb-6 overflow-x-auto pb-1">
                <button
                  onClick={() => setTab('ai')}
                  className={`px-4 py-2 rounded-full transition-all whitespace-nowrap ${
                    tab === 'ai' ? 'bg-gradient-to-r from-sky-400 to-indigo-400 text-white shadow-md' : 'bg-white/60 text-sky-700 dark:bg-slate-800/60 dark:text-sky-300'
                  }`}
                  data-testid="tab-ai"
                >
                  <Sparkles className="w-4 h-4 inline mr-2" />
                  {t('mindfulness.aiGuided')}
                </button>
                <button
                  onClick={() => setTab('stories')}
                  className={`px-4 py-2 rounded-full transition-all whitespace-nowrap ${
                    tab === 'stories' ? 'bg-gradient-to-r from-indigo-400 to-violet-400 text-white shadow-md' : 'bg-white/60 text-sky-700 dark:bg-slate-800/60 dark:text-sky-300'
                  }`}
                  data-testid="tab-stories"
                >
                  <Moon className="w-4 h-4 inline mr-2" />
                  {t('mindfulness.sleepStoriesTab')}
                </button>
                {/* Non-AI meditation & breathing tabs hidden — revisit after stable revenue */}
              </div>

              {tab === 'ai' && (
                <div className="glass-card p-6">
                  <AIMeditation />
                </div>
              )}

              {tab === 'stories' && (
                <div className="glass-card p-6">
                  <SleepStories />
                </div>
              )}

              {/* Non-AI meditation & breathing content hidden — revisit after stable revenue
              {tab === 'meditation' && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {sessions.map((session) => {
                    const Icon = getCategoryIcon(session.category);
                    return (
                      <div
                        key={session.id}
                        className="glass-card glass-card-hover p-6 cursor-pointer"
                        onClick={() => setActiveSession(session)}
                        data-testid={`session-${session.id}`}
                      >
                        <div className="flex items-start gap-4">
                          <div className="w-14 h-14 rounded-xl bg-pink-100 flex items-center justify-center">
                            <Icon className="w-7 h-7 text-pink-500" strokeWidth={1.5} />
                          </div>
                          <div className="flex-1">
                            <h3 className="text-lg font-medium text-sky-800">{session.name}</h3>
                            <p className="text-sm text-slate-500 mt-1">{session.description}</p>
                            <div className="flex items-center gap-2 mt-3">
                              <Clock className="w-4 h-4 text-slate-400" />
                              <span className="text-sm text-slate-500">{session.duration_minutes} {t('common.minutes')}</span>
                              {session.audio_url && (
                                <span className="flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-green-100 text-green-600">
                                  <Volume2 className="w-3 h-3" /> Audio
                                </span>
                              )}
                            </div>
                          </div>
                          <Play className="w-8 h-8 text-pink-400" />
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}

              {tab === 'breathing' && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {exercises.map((exercise) => (
                    <div
                      key={exercise.id}
                      className="glass-card glass-card-hover p-6 cursor-pointer"
                      onClick={() => setActiveExercise(exercise)}
                      data-testid={`exercise-${exercise.id}`}
                    >
                      <div className="flex items-start gap-4">
                        <div className="w-14 h-14 rounded-xl bg-cyan-100 flex items-center justify-center">
                          <Wind className="w-7 h-7 text-cyan-500" strokeWidth={1.5} />
                        </div>
                        <div className="flex-1">
                          <h3 className="text-lg font-medium text-sky-800">{exercise.name}</h3>
                          <p className="text-sm text-slate-500 mt-1">{exercise.description}</p>
                          <div className="flex items-center gap-4 mt-3 text-xs text-slate-500">
                            <span>{exercise.pattern.inhale}-{exercise.pattern.hold1}-{exercise.pattern.exhale}-{exercise.pattern.hold2}</span>
                            <span>{exercise.cycles} cycles</span>
                          </div>
                        </div>
                        <Play className="w-8 h-8 text-cyan-400" />
                      </div>
                    </div>
                  ))}
                </div>
              )}
              end of hidden non-AI sections */}
            </>
          ) : activeSession ? (
            <div className="glass-card p-8">
              <MeditationTimer 
                session={activeSession}
                onComplete={handleMeditationComplete}
                onBack={() => setActiveSession(null)}
                t={t}
              />
            </div>
          ) : (
            <div className="glass-card p-8">
              <BreathingTimer
                exercise={activeExercise}
                onComplete={handleBreathingComplete}
                onBack={() => setActiveExercise(null)}
                t={t}
              />
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

const MindfulnessGuarded = () => (
  <Mindfulness />
);

export default MindfulnessGuarded;
